import React from "react";
import { IContextProvider } from "../../uxp";
import { IErrorProps } from "../designer/core/designer-types";
import { IDataSource, IDataSourceBinding, schemaAtJsonPath } from "./data-source-picker/datasource";
import { hasValue, loadScriptFileAsync } from "../../util";
import {
    IBaseProperty,
    ICollectionAttributeTypeDefinition,
    IPropertyEditorProps,
    ISerializedUIField,
    IUIFieldDefinition,
    IUIPropertyDefinition,
    IWDDesignModeProps,
    IWidgetJSON,
    IWidgetTemplateInstanceProps,
    IWidgetTemplateProps,
    TCollectionAttributeMetaTypeName,
} from "./common-types";
import { findCardTemplate } from "./common-template-registry";

// Re-export the extracted modules so every existing `from ".../common"` import
// keeps working unchanged.
export * from "./common-types";
export * from "./common-paths";
export * from "./common-template-registry";

export function canShowField(field: string, configuration: string[]) {
    if (!configuration || configuration?.length == 0) return true
    return !configuration?.includes(field)
}

export function wrapPropertyEditor<T>(editor: React.FunctionComponent<IPropertyEditorProps<T>>, defaults: T, hideFields?: string[]): IUIPropertyDefinition<T> {
    return {
        defaults, render: (props, uxpContext, contextData, containerId, customProps) => {
            return editor({ ...props, hideFields: hideFields });
        }
    };
}

export const AvailableAttributeMetaTypeDefinitions: ICollectionAttributeTypeDefinition[] = [
    {
        metaType: 'name',
        dataType: 'text',
        label: 'Name',
    },
    {
        metaType: 'email',
        dataType: 'text',
        label: 'Email',
    },
    {
        metaType: 'number',
        dataType: 'number',
        label: 'Number',
    },
    {
        metaType: 'date',
        dataType: 'datetime',
        label: 'Date',
    },
    {
        metaType: 'time',
        dataType: 'datetime',
        label: 'Time',
    },
    {
        metaType: 'text',
        dataType: 'text',
        label: 'Text',
    },
    {
        metaType: 'notes',
        dataType: 'text',
        label: 'Long Text',
    },
    {
        metaType: 'true/false',
        dataType: 'boolean',
        label: 'True/False',
    },
    {
        metaType: 'file',
        dataType: 'text',
        label: 'File',
    },
    {
        metaType: 'sensorvalue',
        dataType: 'number',
        label: 'Sensor Value',
    },
    {
        metaType: 'position',
        dataType: 'json',
        label: 'Current Position',
    },
    {
        metaType: 'daterange',
        dataType: 'json',
        label: 'Date Range',
    },
]

export function findDataTypeForAttributeMetaType(metaType: TCollectionAttributeMetaTypeName) {
    return AvailableAttributeMetaTypeDefinitions?.find((a) => a.metaType == metaType)?.dataType
}

let fieldRegister: { [id: string]: IUIFieldDefinition<IBaseProperty> } = {};

export function registerUIField<T extends IBaseProperty>(field: IUIFieldDefinition<T>) {
    if (fieldRegister[field.id]) fieldRegister[`${field.id}###${field.description}`] = field
    else fieldRegister[field.id] = field;
}

export const getEditorFields = () => fieldRegister;
export function getUIFields() {
    return Object.values(fieldRegister);
}
export function getUIFieldMap() {
    let x: { [id: string]: (props: any) => JSX.Element } = {};
    for (let k in fieldRegister) {
        x[k] = fieldRegister[k].field;
    }
    return x;
}

export function getFieldByIdOrKey(field: ISerializedUIField, registeredFields: { [id: string]: IUIFieldDefinition<IBaseProperty> }) {
    if (hasValue(field?.fieldKey) && registeredFields[field?.fieldKey]) return registeredFields[field?.fieldKey]
    return registeredFields[field?.type]
}

export function extractModuleIdFromURL() {
    const searchParams = new URLSearchParams(window.location.search)
    if (!searchParams.has('module')) return
    const moduleId = searchParams.get('module')
    return moduleId
}
export async function extractModuleIdFromURLAndLoadScript() {

    const moduleId = extractModuleIdFromURL()
    // load script file
    if (moduleId == 'uxp-widget-designer-bundle') return
    await loadScriptFileAsync(`/api/UXP/module?id=${moduleId}`, moduleId)
}

export function findAvailableFieldsInSchema(schema: any, path: string, results: any) {
    let targetSchema = (schemaAtJsonPath(schema, path));
    if (targetSchema?.type == 'array') {
        if (targetSchema.items?.type == 'object') {
            let required = Object.keys(targetSchema.items?.properties || {});
            for (let r of required) {
                results[r] = '1';
            }
        }
    }
    if (targetSchema?.type == 'object') {
        let required = Object.keys(targetSchema.properties || {});
        for (let r of required) {
            results[r] = '1';
        }
    }
}

export function findAvailableFields(item: IDataSourceBinding, sources: IDataSource[], results: any) {
    let source = sources?.find(x => x.id == item?.source);
    if (source) {
        let path = item?.path;
        return findAvailableFieldsInSchema(source.schema, path, results);
    }
};

export function validateUIProp(validator: () => boolean, errorId: string, containerId: string, fieldId: string, errorMessage: string, designer: IWDDesignModeProps, errorType: IErrorProps['type'] = 'property', showError = true) {
    if (validator()) {
        if (showError) {
            designer?.addErrors?.([{
                id: errorId,
                type: errorType,
                error: errorMessage,
                containerId: containerId,
                fieldId: fieldId
            }])
        }

        return false
    }
    designer?.removeErrors?.([errorId])
    return true
}

export function generateWidgetFromJSON(widgetJson: IWidgetJSON, uxpContext: IContextProvider) {
    // find the template
    let template = findCardTemplate(widgetJson.type)

    if (!template) {
        console.log("GenerateWidgetFromJSON:: widget template not-found for id", widgetJson.type);
        return null
    }

    return React.createElement(template.template as React.ForwardRefExoticComponent<React.RefAttributes<IWidgetTemplateInstanceProps> & IWidgetTemplateProps>,
        {
            designer: null,
            uiProps: widgetJson.uiProps,
            uxpContext: uxpContext,
            initialSource: null,
            // ref: templateRef,
            // updateWidgetHeight: updateWidgetHeight
        })
}
